import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Chargement, MessageErreur } from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampFiltre } from '../components/ui/Onglets';
import { ChampDate } from '../components/ui/ChampDate';
import { ErreurApi } from '../lib/api';
import { usePerimetre } from '../site/usePerimetre';
import type { ChoixPeriode } from '../lib/useDashboardDirection';
import type { IndicateurSynthese, TonEcart } from '../lib/useSynthesesMensuelles';
import { useAnalytics, type DonneesAnalytics, type FiltreAnalytics, type Modalite } from '../lib/useAnalytics';

/**
 * « Analytics & ROI » (écran `analytics`, TF-479 ; maquette
 * `docs/maquette/modules/05-pages-gestion-communication.js`).
 *
 * Sur la période choisie, comparée à la période précédente : heures formées
 * (et leur courbe sur douze mois, en salle et à distance), taux de complétion
 * des cours en ligne, satisfaction, chiffre d'affaires et impact économique,
 * formations qui forment le plus. Tout vient de l'API, présenté comme la
 * synthèse mensuelle : l'écran ne recompte rien. La heatmap d'engagement et
 * l'export PDF de la maquette n'ont pas de donnée derrière eux : ils ne sont pas
 * repris (décision du 31/10/2026).
 */

type Filtre = Omit<FiltreAnalytics, 'site'>;

const SANS_FILTRE: Filtre = { periode: 'MOIS', du: '', au: '' };

const TONS_ECART: Record<TonEcart, string> = {
  success: 'text-emerald-700',
  danger: 'text-red-700',
  neutral: 'text-codialis-gray',
};

/** Pastille décorative des quatre indicateurs, dans l'ordre de la maquette. */
const TEINTES_INDICATEURS = [
  'from-codialis-sky to-codialis-blue',
  'from-codialis-mint to-codialis-teal',
  'from-amber-500 to-orange-600',
  'from-purple-500 to-fuchsia-600',
];

/** Couleur des chiffres de l'impact économique, dans l'ordre de la maquette. */
const TEINTES_IMPACT = [
  'text-codialis-teal',
  'text-codialis-sky',
  'text-amber-600',
  'text-purple-600',
  'text-codialis-teal',
  'text-codialis-sky',
];

function jourFrancais(jour: string): string {
  return jour.split('-').reverse().join('/');
}

function heureDeMiseAJour(instant: string): string {
  return new Date(instant).toLocaleTimeString('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function pluriel(nombre: number, singulier: string, plurielForme = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielForme : singulier}`;
}

// ---------------------------------------------------------------------------
// Indicateurs
// ---------------------------------------------------------------------------

/** « +134 h · septembre 2026 : 1 100 h », l'écart dans son ton. */
function Comparaison({ indicateur }: { indicateur: IndicateurSynthese }): JSX.Element | null {
  const { comparaison } = indicateur;
  if (!comparaison) return null;
  return (
    <div className="mt-1 text-xs">
      {comparaison.ecart && <span className={`font-bold ${TONS_ECART[comparaison.ton]}`}>{comparaison.ecart}</span>}
      <span className="text-codialis-gray">
        {comparaison.ecart ? ' · ' : ''}
        {comparaison.texte}
      </span>
    </div>
  );
}

function CarteIndicateur({ indicateur, teinte }: { indicateur: IndicateurSynthese; teinte: string }): JSX.Element {
  return (
    <div className="relative min-w-0 overflow-hidden rounded-2xl bg-white p-4 shadow-card sm:p-5">
      <div
        className={`absolute -right-4 -top-4 h-20 w-20 rounded-full bg-gradient-to-br opacity-10 ${teinte}`}
        aria-hidden
      />
      <div className="relative">
        <div className="text-xs uppercase tracking-wide text-codialis-gray">{indicateur.libelle}</div>
        <div className="font-display mt-1 break-words text-3xl font-extrabold text-codialis-navy">
          {indicateur.texte}
        </div>
        <Comparaison indicateur={indicateur} />
        {indicateur.detail && <div className="mt-1 text-xs text-codialis-gray">{indicateur.detail}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Heures formées sur douze mois
// ---------------------------------------------------------------------------

const LIBELLES_MODALITE: Record<Modalite, string> = {
  minutes: 'Toutes modalités',
  presentiel: 'Présentiel',
  distance: 'À distance',
};

function Courbe({ heures, modalite }: { heures: DonneesAnalytics['heures']; modalite: Modalite }): JSX.Element {
  const [actif, setActif] = useState<number | null>(null);
  const { serie } = heures;
  const max = Math.max(0, ...serie.map((point) => point[modalite]));
  const plusHaut = serie.find((point) => point[modalite] === max);
  const positions = serie.map((point, rang) => ({
    x: serie.length > 1 ? (rang / (serie.length - 1)) * 100 : 50,
    // Un dixième de marge en haut : le point le plus haut ne touche pas le bord.
    y: max === 0 ? 100 : 100 - (point[modalite] / (max * 1.1)) * 100,
  }));
  const ligne = positions.map((position) => `${position.x},${position.y}`).join(' L ');
  const point = actif === null ? null : serie[actif];
  const position = actif === null ? null : positions[actif];

  return (
    <div>
      <div className="flex h-48 gap-2">
        <div
          className="flex w-12 shrink-0 flex-col justify-between text-right text-[10px] text-codialis-gray"
          aria-hidden
        >
          <span>{plusHaut?.textes[modalite] ?? ''}</span>
          <span>0 h</span>
        </div>
        <div className="relative min-w-0 flex-1 border-b border-gray-200">
          {/* Le tracé en retrait d'un demi-point : le premier et le dernier ne débordent pas. */}
          <div className="absolute inset-y-0 left-1.5 right-1.5">
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="absolute inset-0 h-full w-full"
              aria-hidden
            >
              <defs>
                <linearGradient id="aireHeures" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" style={{ stopColor: 'rgb(var(--codialis-mint))' }} stopOpacity="0.4" />
                  <stop offset="100%" style={{ stopColor: 'rgb(var(--codialis-mint))' }} stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={`M 0,100 L ${ligne} L 100,100 Z`} fill="url(#aireHeures)" />
              <path
                d={`M ${ligne}`}
                style={{ stroke: 'rgb(var(--codialis-teal))' }}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {positions.map((un, rang) => (
              <span
                key={serie[rang]?.mois}
                className={`absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ${
                  actif === rang ? 'bg-codialis-blue' : 'bg-codialis-teal'
                }`}
                style={{ left: `${un.x}%`, top: `${un.y}%` }}
                aria-hidden
              />
            ))}
            {point && position && (
              <div
                role="tooltip"
                className={`pointer-events-none absolute z-10 -translate-y-[calc(100%+8px)] whitespace-nowrap rounded-lg bg-codialis-navy px-2.5 py-1.5 text-xs text-white shadow-lg ${
                  position.x < 25 ? '' : position.x > 75 ? '-translate-x-full' : '-translate-x-1/2'
                }`}
                style={{ left: `${position.x}%`, top: `${position.y}%` }}
              >
                <div className="font-semibold">{point.textes[modalite]}</div>
                <div className="text-white/80">{point.libelle}</div>
              </div>
            )}
          </div>
          <ol className="absolute inset-0 flex" aria-hidden>
            {serie.map((un, rang) => (
              <li
                key={un.mois}
                className="h-full min-w-0 flex-1 cursor-default"
                onPointerEnter={() => setActif(rang)}
                onPointerLeave={() => setActif(null)}
                onClick={() => setActif(rang)}
              />
            ))}
          </ol>
        </div>
      </div>
      <ol className="ml-14 mt-1.5 flex justify-between text-[10px] text-codialis-gray" aria-hidden>
        {serie.map((un, rang) => (
          <li key={un.mois} className={rang % 2 === 1 ? 'hidden sm:block' : ''}>
            {un.court}
          </li>
        ))}
      </ol>
      {/* Une table ne se réduit pas à 1 px : le conteneur la rogne. */}
      <div className="sr-only">
        <table>
          <caption>Heures formées, mois par mois</caption>
          <thead>
            <tr>
              <th scope="col">Mois</th>
              <th scope="col">Présentiel</th>
              <th scope="col">À distance</th>
              <th scope="col">Ensemble</th>
            </tr>
          </thead>
          <tbody>
            {serie.map((un) => (
              <tr key={un.mois}>
                <td>{un.libelle}</td>
                <td>{un.textes.presentiel}</td>
                <td>{un.textes.distance}</td>
                <td>{un.textes.minutes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CarteHeures({ heures }: { heures: DonneesAnalytics['heures'] }): JSX.Element {
  const [modalite, setModalite] = useState<Modalite>('minutes');
  return (
    <section className="min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <div className="mb-4 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <h2 className="font-display font-bold text-codialis-navy">Heures formées · 12 derniers mois</h2>
          <p className="text-xs text-codialis-gray">
            Suivi mensuel par modalité, {heures.libelle} · feuilles d’émargement clôturées
          </p>
        </div>
        <ChampFiltre valeur={modalite} onChange={(valeur) => setModalite(valeur as Modalite)} etiquette="Modalité">
          {(Object.keys(LIBELLES_MODALITE) as Modalite[]).map((cle) => (
            <option key={cle} value={cle}>
              {LIBELLES_MODALITE[cle]}
            </option>
          ))}
        </ChampFiltre>
      </div>

      {heures.total.minutes === 0 ? (
        <p className="text-sm text-codialis-gray">Aucune heure formée sur ces douze mois.</p>
      ) : (
        <Courbe heures={heures} modalite={modalite} />
      )}

      <dl className="mt-4 grid grid-cols-3 gap-4 border-t border-gray-100 pt-4">
        <div className="min-w-0">
          <dt className="text-xs text-codialis-gray">Présentiel</dt>
          <dd className="font-bold text-codialis-navy">{heures.presentiel.texte}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-codialis-gray">À distance</dt>
          <dd className="font-bold text-codialis-navy">{heures.distance.texte}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-codialis-gray">Sur 12 mois</dt>
          <dd className="font-bold text-codialis-navy">{heures.total.texte}</dd>
        </div>
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Taux de complétion par formation
// ---------------------------------------------------------------------------

function CarteCompletion({ completion }: { completion: DonneesAnalytics['completion'] }): JSX.Element {
  return (
    <section className="min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <div className="mb-4">
        <h2 className="font-display font-bold text-codialis-navy">Taux de complétion par formation</h2>
        <p className="text-xs text-codialis-gray">
          Cours en ligne et quiz suivis par les apprenants en formation aujourd’hui
        </p>
      </div>
      {completion.formations.length === 0 ? (
        <p className="text-sm text-codialis-gray">
          {completion.apprenants === 0
            ? 'Aucun apprenant en formation aujourd’hui.'
            : 'Aucun cours en ligne à suivre pour les apprenants en formation aujourd’hui.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {completion.formations.map((formation) => (
            <li key={formation.code} className="flex items-center gap-3">
              <div className="w-28 min-w-0 shrink-0 sm:w-44">
                <div className="truncate text-sm font-medium text-codialis-navy" title={formation.titre}>
                  {formation.titre}
                </div>
                <div className="text-[11px] text-codialis-gray">{pluriel(formation.apprenants, 'apprenant')}</div>
              </div>
              <div className="progress-track h-2 min-w-0 flex-1 overflow-hidden rounded-full" aria-hidden>
                <div className="progress-bar h-full rounded-full" style={{ width: `${formation.taux}%` }} />
              </div>
              <div className="w-10 shrink-0 text-right text-xs font-bold text-codialis-navy">{formation.texte}</div>
            </li>
          ))}
        </ul>
      )}
      {(completion.autres > 0 || completion.sansCours > 0) && (
        <p className="mt-4 text-xs text-codialis-gray">
          {[
            completion.autres > 0
              ? `${pluriel(completion.autres, 'autre formation', 'autres formations')} avec un taux`
              : null,
            completion.sansCours > 0
              ? `${pluriel(completion.sansCours, 'formation', 'formations')} sans cours en ligne à suivre`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Impact économique et formations
// ---------------------------------------------------------------------------

function CarteImpact({ donnees }: { donnees: DonneesAnalytics }): JSX.Element {
  return (
    <section className="min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-6 lg:col-span-2">
      <h2 className="font-display font-bold text-codialis-navy">Impact économique</h2>
      <p className="mb-4 text-xs text-codialis-gray">
        Sur {donnees.periode.libelle}, comparé à {donnees.comparaison.libelle}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {donnees.impact.map((indicateur, rang) => (
          <div key={indicateur.id} className="min-w-0 rounded-xl bg-gray-50 p-4">
            <div className="text-xs uppercase tracking-wide text-codialis-gray">{indicateur.libelle}</div>
            <div
              className={`font-display mt-1 break-words text-3xl font-extrabold ${TEINTES_IMPACT[rang] ?? 'text-codialis-navy'}`}
            >
              {indicateur.texte}
            </div>
            <Comparaison indicateur={indicateur} />
            {indicateur.detail && <div className="mt-1 text-xs text-codialis-gray">{indicateur.detail}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

function CarteFormations({ donnees }: { donnees: DonneesAnalytics }): JSX.Element {
  return (
    <section className="min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <h2 className="font-display font-bold text-codialis-navy">Top 5 formations · Volume</h2>
      <p className="mb-4 text-xs text-codialis-gray">Heures formées et apprenants en formation sur la période</p>
      {donnees.formations.length === 0 ? (
        <p className="text-sm text-codialis-gray">Aucune formation n’a d’apprenant ni d’heure formée sur la période.</p>
      ) : (
        <ol className="space-y-3">
          {donnees.formations.map((formation, rang) => (
            <li key={formation.code} className="flex items-center gap-3 rounded-lg p-2">
              <div
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-codialis-navy text-xs font-bold text-codialis-mint"
                aria-hidden
              >
                {rang + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-codialis-navy" title={formation.titre}>
                  {formation.titre}
                </div>
                <div className="text-xs text-codialis-gray">{formation.detail}</div>
              </div>
              <div className="shrink-0 text-right text-sm font-bold text-codialis-navy">{formation.heures}</div>
            </li>
          ))}
        </ol>
      )}
      <Link
        to="/dir-vue-globale?onglet=formations"
        className="mt-4 flex min-h-[44px] w-full items-center justify-center text-center text-sm font-medium text-codialis-sky hover:underline"
      >
        Voir le détail par formation
      </Link>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Écran
// ---------------------------------------------------------------------------

function Lecture({ donnees, actualisation }: { donnees: DonneesAnalytics; actualisation: boolean }): JSX.Element {
  const { periode, comparaison } = donnees;
  return (
    <p className="text-xs text-codialis-gray" aria-live="polite">
      <span className="font-semibold text-codialis-navy">Période : {periode.libelle}</span>
      {periode.arreteeAu < periode.au ? `, arrêtée au ${jourFrancais(periode.arreteeAu)}` : ''}
      {' · '}période précédente : {comparaison.libelle}
      {' · '}
      {actualisation ? 'Actualisation…' : `Mis à jour à ${heureDeMiseAJour(donnees.calculeLe)}`}
    </p>
  );
}

export function AnalyticsPage(): JSX.Element {
  const { site } = usePerimetre();
  const [filtre, setFiltre] = useState<Filtre>(SANS_FILTRE);
  const { data, isLoading, isFetching, error } = useAnalytics({
    site,
    ...filtre,
  });

  const choisirPeriode = (periode: ChoixPeriode): void =>
    setFiltre((avant) => ({
      ...avant,
      periode,
      // Des dates personnalisées partent de la période affichée.
      du: periode === 'LIBRE' && !avant.du ? (data?.periode.du ?? '') : avant.du,
      au: periode === 'LIBRE' && !avant.au ? (data?.periode.arreteeAu ?? '') : avant.au,
    }));

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <div className="space-y-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-extrabold text-codialis-navy">Analytics &amp; ROI</h1>
          <p className="mt-1 text-sm text-codialis-gray">
            Pilotage par la donnée · heures, complétion, satisfaction, impact économique
          </p>
        </div>
        {/* Les filtres sous le titre, comme au tableau de bord : la barre des antennes ne l'écrase pas. */}
        <div className="flex flex-wrap items-center gap-2">
          <BarreSites />
          <ChampFiltre
            valeur={filtre.periode}
            onChange={(valeur) => choisirPeriode(valeur as ChoixPeriode)}
            etiquette="Période"
          >
            {(data?.filtres.periodes ?? [{ valeur: 'MOIS', libelle: 'Mois en cours' }]).map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </ChampFiltre>
          {filtre.periode === 'LIBRE' && (
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <ChampDate
                libelle="Du"
                valeur={filtre.du}
                max={filtre.au || undefined}
                onChange={(du) => setFiltre((avant) => ({ ...avant, du }))}
              />
              <ChampDate
                libelle="au"
                valeur={filtre.au}
                min={filtre.du || undefined}
                onChange={(au) => setFiltre((avant) => ({ ...avant, au }))}
              />
            </div>
          )}
        </div>
      </div>

      {data && <Lecture donnees={data} actualisation={isFetching} />}
      {filtre.periode === 'LIBRE' && filtre.du && filtre.au && filtre.du > filtre.au && (
        <MessageErreur message="Le premier jour de la période doit précéder le dernier." />
      )}
      {isLoading && <Chargement libelle="Chargement des indicateurs…" />}
      {!isLoading && error && (
        <MessageErreur
          message={error instanceof ErreurApi ? error.message : 'Impossible de charger les indicateurs.'}
        />
      )}

      {data && (
        <div className={`space-y-6 transition-opacity ${isFetching ? 'opacity-70' : ''}`}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {data.indicateurs.map((indicateur, rang) => (
              <CarteIndicateur
                key={indicateur.id}
                indicateur={indicateur}
                teinte={TEINTES_INDICATEURS[rang % TEINTES_INDICATEURS.length] as string}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <CarteHeures heures={data.heures} />
            <CarteCompletion completion={data.completion} />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <CarteImpact donnees={data} />
            <CarteFormations donnees={data} />
          </div>
        </div>
      )}
    </div>
  );
}
